import { ArrowRight, FilePlus2, PackagePlus, UserPlus, Users, Package, Files } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PERMISSIONS } from '@erp/shared';
import { usePermissions } from '@/features/auth';
import { useFeatureAvailability } from '@/features/platform-features';
import { InlineHelp } from '@/features/help';
import { useState } from 'react';
import { newWorkPath } from '@/app/work-tabs/work-tab-context';

export function SimpleStart() {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const features = useFeatureAvailability();
  const [draftPaths] = useState(() => ({
    order: newWorkPath('/documents/new?type=ORDER'),
    quote: newWorkPath('/documents/new?type=QUOTE'),
    customer: newWorkPath('/partners/new?preset=customer'),
    product: newWorkPath('/products/new'),
  }));
  const tasks = [
    {
      to: draftPaths.order,
      label: 'appearance.newOrder',
      hint: 'appearance.orderHint',
      icon: FilePlus2,
      read: PERMISSIONS.DOCUMENT_READ,
      write: PERMISSIONS.DOCUMENT_WRITE,
    },
    {
      to: draftPaths.quote,
      label: 'appearance.newQuote',
      hint: 'appearance.quoteHint',
      icon: FilePlus2,
      read: PERMISSIONS.DOCUMENT_READ,
      write: PERMISSIONS.DOCUMENT_WRITE,
    },
    {
      to: draftPaths.customer,
      label: 'workTabs.new.customer',
      hint: 'appearance.customerHint',
      icon: UserPlus,
      read: PERMISSIONS.CUSTOMER_READ,
      write: PERMISSIONS.CUSTOMER_WRITE,
    },
    {
      to: draftPaths.product,
      label: 'workTabs.new.product',
      hint: 'appearance.productHint',
      icon: PackagePlus,
      read: PERMISSIONS.PRODUCT_READ,
      write: PERMISSIONS.PRODUCT_WRITE,
    },
  ].filter(
    (task) =>
      can(task.read) && can(task.write) && features.isPathEnabled(task.to.split('?')[0] ?? task.to),
  );
  const lists = [
    {
      to: '/partners/customers',
      label: 'nav.partnerCustomers',
      icon: Users,
      read: PERMISSIONS.CUSTOMER_READ,
    },
    { to: '/products', label: 'products.title', icon: Package, read: PERMISSIONS.PRODUCT_READ },
    { to: '/sales/orders', label: 'nav.salesOrders', icon: Files, read: PERMISSIONS.DOCUMENT_READ },
  ].filter((entry) => can(entry.read) && features.isPathEnabled(entry.to));
  return (
    <section className="simple-start space-y-7" aria-labelledby="simple-start-title">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-primary">{t('appearance.start')}</p>
          <h1 id="simple-start-title" className="mt-2 text-3xl font-semibold tracking-tight">
            {t('appearance.startTitle')}
          </h1>
          <p className="mt-3 max-w-2xl text-muted-foreground">{t('appearance.startHint')}</p>
        </div>
        <InlineHelp articleId="settings.appearance" label={t('help.open')} />
      </header>
      {tasks.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2">
          {tasks.map((task) => (
            <Link
              key={task.to}
              to={task.to}
              className="simple-task flex min-h-32 items-start gap-4 rounded-xl border border-border bg-card p-5 hover:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <task.icon className="mt-1 size-6 shrink-0 text-primary" aria-hidden="true" />
              <div className="flex-1">
                <h2 className="text-lg font-semibold">{t(task.label)}</h2>
                <p className="mt-2 text-sm text-muted-foreground">{t(task.hint)}</p>
              </div>
              <ArrowRight className="mt-1 size-5 shrink-0 text-primary" aria-hidden="true" />
            </Link>
          ))}
        </div>
      )}
      {lists.length > 0 && (
        <div>
          <h2 className="mb-3 text-base font-semibold">{t('appearance.findExisting')}</h2>
          <div className="flex flex-wrap gap-3">
            {lists.map((entry) => (
              <Link
                key={entry.to}
                to={entry.to}
                className="flex min-h-11 items-center gap-3 rounded-lg border bg-card px-4 py-3 text-sm font-medium hover:bg-accent"
              >
                <entry.icon className="size-4" aria-hidden="true" />
                {t(entry.label)}
                <ArrowRight className="size-4" aria-hidden="true" />
              </Link>
            ))}
          </div>
        </div>
      )}
      {tasks.length === 0 && lists.length === 0 && (
        <p className="rounded-lg border p-4 text-muted-foreground">
          {t('appearance.availableAreas')}
        </p>
      )}
    </section>
  );
}
